'use client';

import { includes as _includes } from 'lodash-es';
import { useEffect, Fragment, useMemo } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { useQuery, useSuspenseQuery } from '@apollo/client/react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import { makeStyles } from 'tss-react/mui';
import ErrorMessage from '@/components/error-message';
import NewComment from '@/components/events/new-comment';
import CommentList from '@/components/events/comment-list';
import Host from '@/components/host';
import AttendeesSummary from '@/components/events/attendees-summary';
import JoinToggle from '@/components/events/join-toggle';
import Location from '@/components/events/location';
import LocationOnline from '@/components/events/location-online';
import { eventQuery, eventInviteQuery } from '@/apollo/events';
import EventDate from '@/components/events/date';
import CategoryTags from '@/components/events/category-tags';
import InfoUrlButton from '@/components/events/info-url-button';
import MaterialLink from '@/components/material-link';
import MaterialLinkButton from '@/components/material-link-button';
import NotificationPreferenceToggle from '@/components/events/notification-preference-toggle';
import RideDetailsTable from '@/components/events/ride-details-table';
import ContactInfo from '@/components/events/contact-info';
import RouteMap from '@/components/events/route-map';
import EventSeriesUpcoming from '@/components/events/event-series-upcoming';
import { momentParse } from '@/utils/time';
import UrlLink from '@/components/url-link';
import Markdown from '@/components/markdown';
import ImageUpload from '@/components/image-upload';
import { buildImageUrl } from '@/utils/images';
import { eventTypes } from '@/utils/events';
import { useAppContext } from '@/contexts/app';
import { extractId, eventPath } from '@/utils/slugify';
import EventImageUploads from '@/components/events/image-uploads';
import SectionHeader from '@/components/section-header';
import HostSummaries from '@/components/host-summaries';
import ShareButton from '@/components/share-buttons';
import ShareIcon from '@mui/icons-material/IosShare';
import EventIcon from '@mui/icons-material/Event';
import AddToCalendar from '@/components/events/add-to-calendar';
import ExitToApp from '@mui/icons-material/ExitToApp';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import EventEditDropdown from '@/components/events/edit-dropdown';
import GroupPendingButton from '@/components/events/group-pending-button';
import HostUser from '@/components/host-user';
import HostGroup from '@/components/host-group';
import ApprovalRequiredNotice from '@/components/events/approval-required-notice';
import type { Event } from '@/types/Event';

const useStyles = makeStyles()((theme) => ({
  eventDate: {
    margin: theme.spacing(0, 0, 2, 0),
  },
  hostWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  hostContent: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  hostedBy: {
    marginRight: theme.spacing(0.5),
  },
  infoIcon: {
    flexShrink: 0,
    marginRight: theme.spacing(0.5),
  },
  postedIn: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  cancelledTime: {
    fontSize: 12,
  },
  main: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  about: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  image: {
    maxWidth: 400,
    flexShrink: 0,
    marginLeft: theme.spacing(2),
    '& img': {
      display: 'block',
      width: 'auto',
      height: 'auto',
      maxWidth: '100%',
      maxHeight: 360,
    },
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2, 0),
      maxWidth: '100%',
    },
  },
  editButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  deniedHostWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
  },
  deniedUser: {
    marginRight: theme.spacing(0.5),
  },
  deniedGroup: {
    marginLeft: theme.spacing(0.5),
  },
  deniedMessage: {
    '& > p:last-of-type': {
      marginBottom: 0,
    },
  },
}));

type Series = {
  id: string;
  name: string;
};

type Props = {
  inviteToken?: string;
  autoRsvp?: string;
  upcomingSeriesEvents?: Event[];
  series?: Series | null;
};

const EventDetailPageClient = ({ inviteToken, autoRsvp, upcomingSeriesEvents, series }: Props) => {
  const {
    isLoggedIn,
    login
  } = useAppContext();
  const { id: rawId } = useParams();
  const id = extractId(rawId as string);
  const router = useRouter();
  const { classes } = useStyles();

  const { data, refetch } = useSuspenseQuery(eventQuery, {
    variables: {
      id,
      inviteToken
    }
  });
  const event = data?.event;

  const { data: eventInviteData, loading: eventInviteLoading, error: eventInviteError } = useQuery(eventInviteQuery, {
    variables: {
      id: inviteToken
    },
    skip: !inviteToken
  });
  const eventInvite = eventInviteData?.eventInvite;

  const momentStartAt = useMemo(() => event && event.startAt ? momentParse(event.startAt, event.timezone) : null, [event]);

  useEffect(() => {
    if (event && event.image && event.permissions.editable) {
      new Image().src = buildImageUrl(event.image, 'json-ld');
    }
  }, [event]);

  if (eventInviteError) {
    return (
      <ErrorMessage
        message='An error occurred while loading event'
        error={eventInviteError}
      />
    );
  }
  if (eventInviteLoading) { return null; }
  if (!event) { return null; }

  const { city } = event;

  const hasRsvp = event.membership && _includes(['GOING', 'INTERESTED', 'NOT_GOING'], event.membership.status);

  return (
    <Fragment>
        {series && upcomingSeriesEvents != null && (
          <EventSeriesUpcoming series={series} upcomingEvents={upcomingSeriesEvents} />
        )}

        {event.permissions.accessible && event.status === 'CANCELLED' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h3" sx={{ mt: 0 }}>Event Cancelled</Typography>
            <Markdown>{event.cancellationMessage}</Markdown>
            {event.cancelledAt && (
              <div className={classes.cancelledTime}>
                Cancelled on {momentParse(event.cancelledAt, event.timezone).format('M/D/YYYY')}
              </div>
            )}
          </Alert>
        )}

        {event.status === 'GROUP_PENDING' && event.group.permissions.editable && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="h3" sx={{ mt: 0 }}>Pending Group Approval</Typography>
            <GroupPendingButton event={event} variant="contained" color="primary" />
          </Alert>
        )}

        {event.groupDeniedByUser && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h3" sx={{ mt: 0 }}>Group Approval Denied</Typography>
            <div className={classes.deniedHostWrapper}>
              <HostUser user={event.groupDeniedByUser} simple className={classes.deniedUser} /> denied your event submission to <HostGroup group={event.group} simple className={classes.deniedGroup} />.
            </div>
            <div className={classes.deniedMessage}>
              {event.groupDeniedMessage ? (
                <Markdown>{event.groupDeniedMessage}</Markdown>
              ) : (
                <p><em>No message was given.</em></p>
              )}
            </div>
          </Alert>
        )}

        {event.permissions.editable
          && event.registrationType === 'APPROVAL_REQUIRED'
          && (event.memberSummary?.REQUESTED?.count ?? 0) > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You have {event.memberSummary.REQUESTED.count} pending request{event.memberSummary.REQUESTED.count === 1 ? '' : 's'} —{' '}
            <MaterialLink href={`${eventPath(event.id, event.name)}/attendees`}>Review attendees →</MaterialLink>
          </Alert>
        )}

        {event.registrationType === 'APPROVAL_REQUIRED'
          && event.permissions.accessible
          && event.status === 'PUBLISHED'
          && !event.permissions.approved && (
          <ApprovalRequiredNotice event={event} onJoin={() => refetch({ id: event.id })} />
        )}

        {event.registrationType === 'EXTERNAL_REGISTRATION' && event.permissions.accessible && event.status === 'PUBLISHED' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="h3" sx={{ mt: 0 }}>External registration required</Typography>
            <Typography variant="body2" sx={{ mb: event.infoUrl ? 1 : 0 }}>
              RSVP here to track your interest, but you must register on the organizer&#39;s site to secure your spot.
            </Typography>
            {event.infoUrl && (
              <Button
                href={event.infoUrl}
                rel="noopener noreferrer"
                variant="contained"
                color="warning"
                size="small"
                endIcon={<ExitToApp />}
              >
                Register ({event.infoUrl.replace(/^https?:\/\//, '').replace(/\/.*$/, '')})
              </Button>
            )}
          </Alert>
        )}

        <Box className={classes.main} sx={{ mb: 0 }}>
          <div className={classes.about}>
            {event.permissions.accessible && (
              <Fragment>
                <div>
                  <JoinToggle
                    event={event}
                    eventInvite={eventInvite}
                    autoRsvp={autoRsvp}
                    onRsvp={() => {
                      if (autoRsvp) {
                        router.replace(eventPath(event.id, event.name));
                      }
                    }}
                    onJoin={() => {
                      refetch({ id: event.id });
                    }}
                  />
                  <AttendeesSummary event={event} />
                </div>

                <EventDate event={event} />

                {event.eventType === 'ONLINE_EVENT' ? (
                  <LocationOnline event={event} />
                ) : (
                  <Fragment>
                    <Location event={event} type="start" />
                    <Location event={event} type="end" />
                  </Fragment>
                )}

                <div className={classes.hostWrapper}>
                  <PersonIcon className={classes.infoIcon} />
                  <div className={classes.hostContent}>
                    <span className={classes.hostedBy}>Hosted by</span>
                    <Host event={event} />
                  </div>
                </div>

                {event.visibility === 'PUBLIC' && (
                  <div className={classes.postedIn}>
                    <PublicIcon className={classes.infoIcon} />
                    <span>
                      Posted in{' '}
                      <MaterialLink href={`/cities/${city.slug}`}>
                        {city.name}
                      </MaterialLink>
                    </span>
                  </div>
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <ShareButton
                    title={`${event.name}${momentStartAt ? ` on ${momentStartAt.format('ddd, MMMM D, YYYY')}` : ''}`}
                    path={eventPath(event.id, event.name)}
                    variant="outlined"
                    size="small"
                    startIcon={<ShareIcon />}
                  />
                  <AddToCalendar
                    event={event}
                    variant="outlined"
                    size="small"
                    startIcon={<EventIcon />}
                  >
                    Add to Calendar
                  </AddToCalendar>
                </Box>
              </Fragment>
            )}
          </div>

          {event.permissions.accessible && event.image && (
            <div className={classes.image}>
              <ImageUpload record={event} size="400x600iwe" linkToFull />
            </div>
          )}
        </Box>

        {event.permissions.accessible ? (
          <Fragment>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h2">Details</Typography>

              <Markdown>{event.description}</Markdown>

              {event.permissions.approved && event.registrationType === 'APPROVAL_REQUIRED' && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="h3" sx={{ mt: 0 }}>Approved attendees</Typography>
                  {event.approvalDescription ? (
                    <Markdown>{event.approvalDescription}</Markdown>
                  ) : (
                    "You're approved! All event details are now visible."
                  )}
                </Alert>
              )}

              {!event.permissions.approved && !eventInvite && (
                <p><em>{eventTypes[event.eventType].name} details omitted pending approval</em></p>
              )}

              <InfoUrlButton event={event} />

              {event.eventType === 'RIDE' && (
                <RideDetailsTable event={event} />
              )}

              <CategoryTags event={event} showRideStats={false} />

              {event.routeUrl && (
                <Fragment>
                  <Typography variant="h3">Route</Typography>
                  <UrlLink url={event.routeUrl} raw />
                  <RouteMap event={event} />
                </Fragment>
              )}

              <ContactInfo event={event} />

            {(event.permissions.editable || hasRsvp) && (
              <div className={classes.editButtons}>
                {event.permissions.editable && (
                  <Fragment>
                    <EventEditDropdown
                      variant="outlined"
                      event={event}
                    />
                    <MaterialLinkButton
                      href={`${eventPath(event.id, event.name)}/attendees`}
                      variant="outlined"
                    >
                      Manage Attendees
                    </MaterialLinkButton>
                    {!event.series && event.status !== 'CANCELLED' && (
                      <MaterialLinkButton
                        href={`/event-series/new?convertEventId=${event.id}`}
                        variant="outlined"
                        startIcon={<EventRepeatIcon />}
                      >
                        Convert to series
                      </MaterialLinkButton>
                    )}
                  </Fragment>
                )}

                {hasRsvp && (
                  <MaterialLinkButton
                    href={`${eventPath(event.id, event.name)}/settings`}
                    variant="outlined"
                  >
                    RSVP Settings
                  </MaterialLinkButton>
                )}
              </div>
            )}
            </Box>

            <HostSummaries event={event} />

            {event.permissions.approved && !eventInvite && (
              <Fragment>
                <EventImageUploads event={event} id="discussion" />

                <SectionHeader action={<NotificationPreferenceToggle event={event} />}>
                  Discussion
                </SectionHeader>
                <CommentList event={event} />
                <NewComment event={event} />
              </Fragment>
            )}
          </Fragment>
        ) : (
          <Alert severity="info">
            <Typography variant="body2">
              You do not have access to this event.
            </Typography>
            {!isLoggedIn && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => {
                  login(autoRsvp ? `${eventPath(event.id, event.name)}?rsvp=${autoRsvp}` : eventPath(event.id, event.name));
                }}
                sx={{ mt: 1 }}
              >
                Log In
              </Button>
            )}
          </Alert>
        )}
    </Fragment>
  );
};

export default EventDetailPageClient;
